import React, { useContext, FC } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import Blade, { BladeBody, BladeContainer, BladeFooter } from '../Blade';
import { Typography } from '../Typography';
import { Spacer } from '../Spacer';
import { My_Kfh_Read_Saved_SearchesDocument, useMykfh_Create_Saved_SearchMutation } from '../../types/graphqlHelpers';
import { UiStateContext } from '../../context/UiStateContext';
import { Panel } from '../Panel';
import { Input } from '../forms/Input';
import { Radio } from '../forms/Radio';
import { Divider } from '../Divider';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { SmallPrint } from '../forms/SmallPrint';
import { isBrowser } from '../../utils/isBrowser';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { RecaptchaAction } from '../forms/RecaptchaAction';

interface CreatePropertyAlertBladeProps {
  defaultName?: string;
  apiPath: string;
  locations: string;
  category: string;
}

const ToastMessage = ({ title }) => (
  <div>
    <Typography variant="titleSmall">Property alert created</Typography>
    <Typography variant="bodyLarge">{title}</Typography>
  </div>
);

export const CreatePropertyAlertBlade: FC<CreatePropertyAlertBladeProps> = ({
  defaultName,
  apiPath,
  locations,
  category,
}) => {
  const { setActiveBlade } = useContext(UiStateContext);
  const [myKfhCreateSavedSearchMutation, { data: response, loading, error }] = useMykfh_Create_Saved_SearchMutation();
  const url = isBrowser ? `${window.location.pathname}${window.location.search}` : '';
  const { dataLayerPush } = useDataLayerPush();

  const formMethods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      name: defaultName || '',
      alertPreference: 'instant',
    },
  });

  const { register, handleSubmit, control, formState, reset } = formMethods;
  const { errors, dirtyFields, touchedFields, isSubmitted } = formState;

  const { handleReCaptchaVerify, isHuman } = useRecaptcha({ formMethods, action: 'createPropertyAlert' });

  // Function to handle collecting form data and  submitting the GQL mutation
  const onSubmit = (formData) => {
    delete formData.captchaToken;
    const { name, alertPreference } = formData;

    // Work out alert preferences:

    let deliveryFrequency;
    let deliveryDay;
    let deliveryTime;
    let instantAlert = true;
    let summaryAlert = false;

    if (alertPreference === 'instant') {
      deliveryFrequency = 'Hourly';
      deliveryDay = '';
      deliveryTime = '';
      instantAlert = true;
      summaryAlert = false;
    } else if (alertPreference === 'daily') {
      deliveryFrequency = 'Daily';
      deliveryDay = '';
      deliveryTime = '09:00';
      instantAlert = false;
      summaryAlert = true;
    } else if (alertPreference === 'weekly') {
      deliveryFrequency = 'Weekly';
      deliveryDay = 'Monday';
      deliveryTime = '09:00';
      instantAlert = false;
      summaryAlert = true;
    } else if (alertPreference === 'noAlerts') {
      instantAlert = false;
      summaryAlert = false;
    }

    myKfhCreateSavedSearchMutation({
      variables: {
        myKfhCreateSavedSearch: {
          name,
          apiPath,
          url: isBrowser ? window.location.href : '',
          alertSettings:
            alertPreference === 'noAlerts'
              ? null
              : [
                  {
                    deliveryFrequency,
                    deliveryDay,
                    deliveryTime,
                  },
                ],
          payload: {
            instantAlert,
            summaryAlert,
            locations,
            category,
          },
        },
      },
      refetchQueries: [
        {
          query: My_Kfh_Read_Saved_SearchesDocument,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        dataLayerPush({
          event: 'formSubmit',
          formName: 'createPropertyAlert',
          formValidation: 'success',
          formError: undefined,
        });

        reset({
          name: defaultName || '',
          alertPreference: 'instant',
        });

        setActiveBlade(null);
        toast.success(<ToastMessage title={formData.name} />);

        dataLayerPush({
          event: 'formSubmit',
          formName: 'createPropertyAlert',
          formValidation: 'success',
          formError: undefined,
        });
      },
    });
  };

  return (
    <Blade id="createPropertyAlert" customBlade>
      <FormProvider {...formMethods}>
        <BladeContainer as="form" onSubmit={handleSubmit(onSubmit)} method="post" noValidate>
          <BladeBody>
            <Spacer>
              <Spacer spacing="half">
                <Typography variant="displaySmall">Create alert</Typography>
              </Spacer>
              <Input
                type="text"
                invalid={!!errors.name}
                valid={touchedFields.name && dirtyFields.name && !errors.name}
                label="Alert name"
                id="name"
                {...register('name', { required: true })}
                required
              />
              <Panel as="fieldset" border>
                <Spacer>
                  <Typography component="legend" variant="titleMedium">
                    Send an email alert when a new property is added
                  </Typography>
                  <Spacer spacing="half">
                    <Radio
                      label="Instant alert"
                      id="instant"
                      {...register('alertPreference')}
                      value="instant"
                      helpText="We’ll email you as soon as we put a property on the market that matches your search criteria."
                    />
                    <Divider />
                    <Radio label="Daily" id="daily" {...register('alertPreference')} value="daily" />
                    <Divider />
                    <Radio label="Weekly" id="weekly" {...register('alertPreference')} value="weekly" />
                    <Divider />
                    <Radio label="Don't alert me" id="noAlerts" {...register('alertPreference')} value="noAlerts" />
                  </Spacer>
                </Spacer>
              </Panel>
              <RecaptchaAction show={!isHuman} handleReCaptchaVerify={handleReCaptchaVerify} />
              <SmallPrint />
            </Spacer>
          </BladeBody>
          <BladeFooter
            primaryButton={{
              label: 'Create alert',
              onClick: handleSubmit(onSubmit),
              disableClickTracking: true,
            }}
            secondaryButton={{
              label: 'Cancel',
              onClick: () => {
                reset({
                  name: defaultName || '',
                  alertPreference: 'instant',
                });
                setActiveBlade(null);
              },
              type: 'button',
            }}
          />
        </BladeContainer>
      </FormProvider>
    </Blade>
  );
};
